// Pure helpers for the calendar deep link `/calendar?m=&d=&e=` (THE-746
// Phase 2, PR 2.5). One module so the WRITER (ComingUpStrip, in
// components/) and the READER (EventList) agree on the param names and
// the event-id shape by construction instead of by two hand-kept copies.
//
// Kept free of React and of Firebase so it unit-tests without rendering,
// same rationale as personEvents.ts / initialScrollAnchor.ts.
import type { PersonEventType } from './mockCalendarData';

/** `?m=` — the month CalendarPage derives its cursor from. */
export const MONTH_PARAM = 'm';
/** `?d=` — the day key, matching an event card's `data-event-date`. */
export const DAY_PARAM = 'd';
/** `?e=` — the exact calendar event id, matching `data-event-id`. */
export const EVENT_PARAM = 'e';

const DAY_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * The calendar event id for a recipient's birthday/anniversary occurrence.
 *
 * This shape is ALSO built in two other places — `personEvents.ts`
 * (`recipientToYearOccurrences`) for every visible year, and CalendarPage's
 * private `comingUpEventToCalendarEvent` for the materialized future year —
 * because those two deliberately collide so the rail dedups them. A deep
 * link's `?e=` has to name the same id or it resolves to nothing, so the
 * format lives here once and `calendarDeepLink.test.ts` pins it against
 * `recipientToYearOccurrences`'s real output rather than against a
 * transcribed string.
 */
export function personCalendarEventId(
  recipientId: string,
  eventType: PersonEventType,
  year: number,
): string {
  return `${recipientId}_${eventType}_${year}`;
}

/**
 * The calendar event id for one year's occurrence of a holiday.
 *
 * Here for the same reason `personCalendarEventId` is: the id is ALSO built by
 * `holidayEvents.holidayToCalendarEvent`, which is what the event rail
 * actually renders, and a deep link's `?e=` has to name that exact id or it
 * resolves to nothing. `holidayToCalendarEvent` calls THIS function rather
 * than interpolating its own copy, and `calendarDeepLink.test.ts` pins the
 * two together against its real output.
 *
 * NOTE FOR ANALYTICS: this id embeds the holiday's `eventType` — the specific
 * holiday NAME. It is fine in the URL bar (the user's own browser), and it is
 * why `?e=` is scrubbed out of GA4's `page_location` in `lib/gaPixel.ts`.
 * THE-746's ground rule is that holiday choices never leave our database.
 */
export function holidayCalendarEventId(eventType: string, year: number): string {
  return `holiday_${eventType}_${year}`;
}

/**
 * Build `/calendar?m=YYYY-MM&d=YYYY-MM-DD&e=<id>`.
 *
 * `m` is derived from `dayIso` rather than passed in, so the month the page
 * opens on can never disagree with the day it scrolls to.
 */
export function buildCalendarDeepLink(dayIso: string, eventId?: string): string {
  const params = new URLSearchParams();
  params.set(MONTH_PARAM, dayIso.slice(0, 7));
  params.set(DAY_PARAM, dayIso);
  if (eventId) params.set(EVENT_PARAM, eventId);
  return `/calendar?${params.toString()}`;
}

/**
 * Erase `?d=` and `?e=` from the LIVE browser URL, keeping `?m=`, every other
 * param, and the hash. Replaces the current history entry — the deep link is
 * not a separate place, and Back must leave the calendar in one press
 * (CalendarPage's month writes have the same rule).
 *
 * THE-784. This is the WRITER-SIDE half that `lib/sensitiveUrlParams.ts` is the
 * backstop for, and it closes the one leak a payload scrub structurally cannot:
 * Meta's `fbq()` reads `document.location.href` itself and sends it as `dl`,
 * inside third-party code we neither author nor can hand a scrubbed value to. A
 * Coming Up holiday card mints `?e=holiday_christmas_2026`, so until the id is
 * out of the address bar Meta receives the holiday NAME verbatim — THE-746's
 * ground rule, not an inference from a date. Removing it from the browser fixes
 * every present and future vendor at once instead of one payload at a time.
 *
 * `?m=` deliberately survives, for the same reason it survives the analytics
 * scrub: a month is the page's actual content and names no holiday on its own
 * (December covers Christmas, Hanukkah, Kwanzaa and New Year's), and it is what
 * CalendarPage derives its cursor from — dropping it would snap the user back
 * to today's month.
 *
 * Called only ONCE the deep-link scroll has RESOLVED (see EventList's effect),
 * never on mount. The accepted consequence, identical to the `?g=` claim
 * token's and deliberately not a third behaviour invented for holidays: a link
 * someone pasted into a message still resolves, but an address bar copied after
 * the page has loaded has lost the target and opens the month view.
 *
 * Defensive: `replaceState` can throw in odd embeds (sandboxed iframes, some
 * in-app webviews). A failed cleanup must not take the rail down with it — the
 * scroll has already happened by then, and the analytics scrub still covers
 * every payload we build ourselves.
 */
export function stripCalendarDeepLinkParams(): void {
  try {
    eraseDeepLinkParamsFromLiveUrl();
  } catch {
    /* Leave the URL as-is; the scroll already resolved from the in-memory read. */
  }
}

/**
 * Delete `?d=`/`?e=` from the LIVE URL via `replaceState`, keeping `?m=`, every
 * other param and the hash. Returns the search string as it was BEFORE the
 * erase, or null when there was nothing to erase (in which case the URL is left
 * byte-identical — not rewritten to an equal value).
 *
 * The single copy of the erase, shared by the two callers that need it at
 * different times: `stripCalendarDeepLinkParams` (THE-784, after the scroll
 * resolves) and `captureCalendarDeepLink` (THE-787, before the arrival's
 * PageView). Deliberately throws rather than swallowing, because the two
 * callers want different things from a failure.
 */
function eraseDeepLinkParamsFromLiveUrl(): string | null {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  if (!params.has(DAY_PARAM) && !params.has(EVENT_PARAM)) return null;
  params.delete(DAY_PARAM);
  params.delete(EVENT_PARAM);
  const qs = params.toString();
  const next = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
  window.history.replaceState(window.history.state, '', next);
  return search;
}

/** `/calendar`, with or without a trailing slash. The capture below is scoped
 *  to it so a root-level hook can never eat a `?d=`/`?e=` belonging to some
 *  other route's reader. */
const CALENDAR_PATH_RE = /^\/calendar\/?$/i;

/**
 * The deep link of the most recent `/calendar` arrival, paired with the exact
 * href the capture left in the address bar when it took it.
 *
 * `cleanedHref` is what makes the stash safe to never clear. It is checked on
 * read, so the stash is live for exactly as long as the URL the capture
 * produced is still the URL on screen — which is the EventList mount that
 * arrival caused, and nothing after it. Two things fall out of that:
 *
 *   - StrictMode's double-invoked effects are a no-op. The second run sees a
 *     URL with no `?d=`/`?e=` left, so it neither re-stashes nor clears.
 *   - A later bare `/calendar` (the Calendar tab, after the user has wandered
 *     off and come back) does NOT resurrect the old link. Without the check it
 *     would: EventList would read a December deep link on a page whose cursor
 *     is today's month, find no card, and — per its own precedence rules —
 *     outrank CalendarPage's automatic initial anchor while it waited for one.
 */
let capturedDeepLink: CapturedDeepLink | null = null;

/** The raw arrival search, paired with the href the erase left behind. */
interface CapturedDeepLink {
  search: string;
  cleanedHref: string;
}

/**
 * The same stash, written in raw ES5 from `public/index.html` instead of from
 * this module. THE-790.
 *
 * The bootstrap exists because `captureCalendarDeepLink` below — a React effect
 * — cannot be ordered against gtag.js. gtag initialises itself from `<head>`,
 * snapshots `document.location.href` as it does, and then replays that snapshot
 * as `dl` on every enhanced-measurement event (scroll, click, outbound, search)
 * and as `dr` on the next page_view. Nothing we write inside React precedes it;
 * whether the effect won was decided by main-bundle arrival time, which made the
 * leak a race (clean 6/6 warm, dirty 2/2 on a slow load — real browser, prod,
 * 2026-10-03). The `<head>` script is above the tags in document order, so the
 * dirty URL is gone before any vendor script can run.
 *
 * Declared here, next to the reader, so the two halves of one contract are in
 * one place. `calendar/__tests__/calendarDeepLinkBootstrap.test.ts` runs the
 * shipping bytes of that script and fails on drift.
 */
declare global {
  interface Window {
    __theaCalendarDeepLink?: CapturedDeepLink;
  }
}

/**
 * The bootstrap's stash, shape-checked.
 *
 * Shape-checked rather than trusted because this crosses a module boundary that
 * the type system does not cover: the writer is hand-written ES5 in an HTML
 * file. A malformed value must read as "no capture" and send EventList to its
 * `window.location.search` fallback, never hand it a non-string.
 */
function readBootstrapCapture(): CapturedDeepLink | null {
  const stash = typeof window === 'undefined' ? undefined : window.__theaCalendarDeepLink;
  if (!stash) return null;
  return typeof stash.search === 'string' && typeof stash.cleanedHref === 'string'
    ? stash
    : null;
}

/**
 * Take `?d=`/`?e=` off the live URL and stash them in memory, BEFORE anything
 * on this commit reads `document.location.href`.
 *
 * THE-787, and the one leak THE-784's strip structurally cannot reach. The
 * strip lives inside EventList, which lives behind `React.lazy` — so on a warm
 * SPA navigation (a Coming Up holiday card tap) the commit that handles the
 * navigation renders a Suspense fallback, `usePageTracking`'s root effect fires
 * `fbq('track','PageView')`, and fbevents.js reads `document.location.href` for
 * its automatic `dl` while the calendar chunk is still in flight. That one hit
 * per arrival carried `e=holiday_christmas_2026` — the holiday NAME, which is
 * exactly what THE-746's ground rule is about.
 *
 * Erasing above the pageview effect is the only ordering that beats it, so this
 * is called from a root hook declared BEFORE `usePageTracking()` (see
 * `useCalendarDeepLinkCapture`, and PageTrackingMount in `src/App.js`). Effects
 * within one component run in declaration order, on a cold load and on an SPA
 * navigation alike.
 *
 * This REVERSES THE-784's "replaceState once the scroll RESOLVES, not on
 * mount", and that reversal is approved rather than accidental. The rule
 * existed because the URL was the deep link's only source of truth, so erasing
 * it early destroyed targets that had not landed yet. It no longer is: the raw
 * search goes into `capturedDeepLink` first and `readCapturedCalendarDeepLink`
 * hands it to EventList, so a provisional day landing can still upgrade to the
 * exact event id with the address bar already clean. Nothing is lost —
 * `EventList.deepLink.test.tsx`'s late-arriving-card case is the proof.
 *
 * THE-784's resolve-first strip STAYS, as the defence for any EventList mount
 * where this hook is absent: the stories, `EventList.dedupe.test.tsx`, and any
 * future bare mount. The two are not redundant; they cover different mounts.
 *
 * Defensive: `replaceState` can throw in odd embeds (sandboxed iframes, some
 * in-app webviews). On a throw nothing is stashed, so EventList falls back to
 * `window.location.search` — the link still resolves and the residual `dl` is
 * exactly what it was before this function existed. Never worse than the
 * baseline, and never a broken deep link.
 */
export function captureCalendarDeepLink(pathname: string): void {
  if (!CALENDAR_PATH_RE.test(pathname)) return;
  try {
    const search = eraseDeepLinkParamsFromLiveUrl();
    if (search === null) return;
    capturedDeepLink = { search, cleanedHref: window.location.href };
  } catch {
    /* URL untouched and nothing stashed — EventList reads the URL instead. */
  }
}

/**
 * The raw `location.search` of the arrival `captureCalendarDeepLink` took, or
 * null if there was no capture (or the URL has moved on since — see
 * `capturedDeepLink`).
 *
 * EventList's mount-time read is `readCapturedCalendarDeepLink() ??
 * window.location.search`. The fallback is the half that keeps EventList
 * renderable without a Router or a root hook, which its own comment calls out
 * as deliberate.
 */
export function readCapturedCalendarDeepLink(): string | null {
  // In-module stash first, bootstrap stash second. On a cold deep-link load only
  // the bootstrap has one — it erased the params during HTML parse, so the
  // effect below found nothing left to take and deliberately stashed nothing.
  // On a warm SPA navigation it is the other way round: no document loaded, so
  // the bootstrap never ran again and the effect is the only writer. Preferring
  // the in-module one keeps the FRESHER capture winning in either order.
  const stash = capturedDeepLink ?? readBootstrapCapture();
  if (!stash) return null;
  // The href pair is what makes either stash safe to never clear: it is live for
  // exactly as long as the URL its erase produced is still on screen.
  return stash.cleanedHref === window.location.href ? stash.search : null;
}

/** A deep link's scroll intent: an exact event, a day, or both. */
export interface CalendarDeepLinkTarget {
  /** From `?e=`. Null on old links, shared links and hand-typed URLs. */
  eventId: string | null;
  /** From `?d=`, shape-validated. The fallback when `eventId` is absent or
   *  names an event that isn't on the page. */
  dayIso: string | null;
}

/**
 * Read the scroll intent out of a `location.search` string. Returns null
 * when there is nothing to scroll to, so callers can treat "no deep link"
 * and "a deep link carrying only `?m=`" identically.
 *
 * A malformed `d` reads as absent (same policy as CalendarPage's
 * `parseMonthParam`): a hand-typed URL should land on the month and do
 * nothing, not throw and not scroll somewhere arbitrary. `e` is NOT
 * pattern-checked — ids are opaque and a miss already falls back to the
 * day — but it is matched by attribute comparison, never interpolated into
 * a selector.
 */
export function readCalendarDeepLink(search: string): CalendarDeepLinkTarget | null {
  const params = new URLSearchParams(search);
  const rawEvent = params.get(EVENT_PARAM);
  const rawDay = params.get(DAY_PARAM);
  const eventId = rawEvent && rawEvent.length > 0 ? rawEvent : null;
  const dayIso = rawDay && DAY_RE.test(rawDay) ? rawDay : null;
  if (!eventId && !dayIso) return null;
  return { eventId, dayIso };
}
